import React, { useState, useEffect, useCallback } from 'react';
import { Spin } from 'antd';
import {
    ThunderboltOutlined,
    CheckCircleOutlined,
    WarningOutlined,
    ReloadOutlined,
    DatabaseOutlined,
} from '@ant-design/icons';
import { useReports } from '../context/ReportsContext';
import { getAiSummary, refreshAiSummary } from '../api/reportsApi';
import moment from 'moment';

export default function AiManagementSummaryCard() {
    const { filters, refreshKey } = useReports();
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState(null);

    // Initial fetch from DB (if not saved, backend computes and saves once to DB)
    const fetchSummary = useCallback(async (forceRefresh = false) => {
        setLoading(true);
        try {
            const payload = {
                period: filters.period,
                startDate: filters.startDate,
                endDate: filters.endDate,
                department: filters.department,
                teamMember: filters.teamMember,
                projectType: filters.projectType,
            };

            const res = forceRefresh
                ? await refreshAiSummary(payload)
                : await getAiSummary(payload);

            if (res && res.success && res.result) {
                setData(res.result);
            }
        } catch (err) {
            console.error('Failed to load AI summary:', err);
        } finally {
            setLoading(false);
        }
    }, [filters.period, filters.startDate, filters.endDate, filters.department, filters.teamMember, filters.projectType]);

    useEffect(() => {
        fetchSummary(false);
    }, [fetchSummary, refreshKey]);

    const handleForceRefresh = () => {
        fetchSummary(true);
    };

    return (
        <div className="relative bg-gradient-to-r from-blue-50/70 via-white to-sky-50/60 dark:from-black dark:via-black dark:to-black border border-blue-200/80 dark:border-neutral-800 rounded-2xl p-5 sm:p-6 shadow-xs transition-all duration-300 mb-6">
            {/* Header matching AiWorkSummaryCard */}
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-blue-100/70 dark:border-neutral-800 pb-4 mb-4">
                <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-xl bg-blue-600/10 text-blue-700 dark:text-blue-400 flex items-center justify-center flex-shrink-0">
                        <ThunderboltOutlined style={{ fontSize: 18 }} />
                    </div>
                    <div>
                        <div className="flex items-center gap-2">
                            <h2 className="text-lg font-bold text-[#003263] dark:text-[#a7c8ff] font-headline tracking-tight">
                                AI Management Summary
                            </h2>
                            {data?.isCachedFromDb && (
                                <span className="inline-flex items-center gap-1 text-xs font-semibold text-blue-700 dark:text-blue-300 bg-blue-100/70 dark:bg-neutral-900 px-2 py-0.5 rounded-full border border-blue-200/60 dark:border-neutral-700">
                                    <DatabaseOutlined style={{ fontSize: 11 }} /> Saved
                                </span>
                            )}
                        </div>
                        <span className="text-xs text-blue-900/70 dark:text-blue-300/70 font-medium">
                            Synthesized intelligence for {filters.period.replace('_', ' ')}
                            {data?.generatedAt && ` • ${moment(data.generatedAt).format('MMM D, h:mm A')}`}
                        </span>
                    </div>
                </div>

                <button
                    type="button"
                    onClick={handleForceRefresh}
                    disabled={loading}
                    className="px-3.5 py-2 rounded-xl text-xs font-bold border border-blue-200 dark:border-neutral-800 bg-white/90 dark:bg-black text-blue-800 dark:text-blue-300 hover:bg-blue-50 dark:hover:bg-neutral-900 transition-all flex items-center gap-2 shadow-2xs cursor-pointer"
                >
                    <ReloadOutlined spin={loading} style={{ fontSize: 12 }} />
                    <span>{loading ? 'Analyzing...' : 'Update Analysis'}</span>
                </button>
            </div>

            {loading && !data ? (
                <div className="py-8 text-center text-sm text-blue-600 dark:text-blue-400">
                    <Spin size="default" /> <span className="ml-2.5 font-medium">Synthesizing executive operational intelligence...</span>
                </div>
            ) : data ? (
                <div>
                    <p className="text-sm sm:text-base text-slate-700 dark:text-slate-200 leading-relaxed font-normal mb-5">
                        {data.summaryText}
                    </p>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Achievements */}
                        <div className="bg-white/95 dark:bg-black rounded-xl p-4 border border-emerald-100 dark:border-neutral-800 shadow-2xs">
                            <div className="text-xs font-bold text-emerald-700 dark:text-emerald-400 mb-2.5 flex items-center gap-2 font-headline uppercase tracking-wider">
                                <CheckCircleOutlined style={{ fontSize: 14 }} /> Notable Achievements &amp; Velocity
                            </div>
                            <ul className="space-y-2 text-sm text-slate-700 dark:text-slate-300 pl-1">
                                {(data.highlights || []).map((h, i) => (
                                    <li key={i} className="flex items-start gap-2.5">
                                        <span className="text-emerald-500 font-bold leading-5">•</span>
                                        <span>{h}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        {/* Risks */}
                        <div className="bg-white/95 dark:bg-black rounded-xl p-4 border border-amber-100 dark:border-neutral-800 shadow-2xs">
                            <div className="text-xs font-bold text-amber-700 dark:text-amber-400 mb-2.5 flex items-center gap-2 font-headline uppercase tracking-wider">
                                <WarningOutlined style={{ fontSize: 14 }} /> Workload &amp; Delivery Risks
                            </div>
                            <ul className="space-y-2 text-sm text-slate-700 dark:text-slate-300 pl-1">
                                {(data.risks || []).map((r, i) => (
                                    <li key={i} className="flex items-start gap-2.5">
                                        <span className="text-amber-500 font-bold leading-5">•</span>
                                        <span>{r}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </div>
            ) : (
                <div className="py-6 text-center text-sm text-slate-400">
                    No summary available. Click "Update Analysis" to generate.
                </div>
            )}
        </div>
    );
}
